import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { useLanguage, api } from '../context/AppContext';
import { safePost } from '../utils/offlineQueue';
import { toast } from 'sonner';
import { 
  RotateCcw, Zap, Loader2, ChefHat, Plus, Check
} from 'lucide-react';

const HIDDEN_ADDITIONS = [
  { name: 'Huile olive', calories: 120, fats_g: 14, protein_g: 0, carbs_g: 0, fiber_g: 0, portion_g: 15, icon: '🫒' },
  { name: 'Beurre', calories: 100, fats_g: 11, protein_g: 0.1, carbs_g: 0, fiber_g: 0, portion_g: 14, icon: '🧈' },
  { name: 'Vinaigrette', calories: 70, fats_g: 7, protein_g: 0, carbs_g: 2, fiber_g: 0, portion_g: 15, icon: '🥗' },
  { name: 'Sauce soya', calories: 10, fats_g: 0, protein_g: 1, carbs_g: 1, fiber_g: 0, portion_g: 15, icon: '🥢' },
  { name: 'Mayonnaise', calories: 100, fats_g: 11, protein_g: 0.1, carbs_g: 0.1, fiber_g: 0, portion_g: 14, icon: '🥄' },
  { name: 'Fromage râpé', calories: 110, fats_g: 9, protein_g: 7, carbs_g: 0.4, fiber_g: 0, portion_g: 28, icon: '🧀' },
  { name: 'Crème sure', calories: 60, fats_g: 5, protein_g: 1, carbs_g: 1, fiber_g: 0, portion_g: 30, icon: '🥛' },
  { name: 'Épices/herbes', calories: 5, fats_g: 0, protein_g: 0, carbs_g: 1, fiber_g: 0, portion_g: 3, icon: '🌿' },
  { name: 'Sucre', calories: 50, fats_g: 0, protein_g: 0, carbs_g: 13, fiber_g: 0, portion_g: 13, icon: '🍬' },
  { name: 'Miel', calories: 65, fats_g: 0, protein_g: 0, carbs_g: 17, fiber_g: 0, portion_g: 21, icon: '🍯' },
];

export function QuickLogChips({ onConfirm, selectedAdditions = [], onToggle }) {
  return (
    <div className="space-y-2" data-testid="quick-log-chips">
      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
        Ingrédients invisibles ajoutés?
      </p>
      <div className="flex flex-wrap gap-1.5">
        {HIDDEN_ADDITIONS.map((item) => {
          const isSelected = selectedAdditions.some(a => a.name === item.name);
          return (
            <button
              key={item.name}
              onClick={() => onToggle(item)}
              data-testid={`chip-${item.name.replace(/\s/g, '-').toLowerCase()}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all
                ${isSelected
                  ? 'bg-primary/20 text-primary border border-primary/50 ring-1 ring-primary/30'
                  : 'bg-secondary/50 text-muted-foreground border border-border hover:bg-secondary hover:text-foreground'
                }`}
            >
              <span>{item.icon}</span>
              <span>{item.name}</span>
              {isSelected && <Check className="w-3 h-3" />}
            </button>
          );
        })}
      </div>
      {selectedAdditions.length > 0 && (
        <div className="flex items-center justify-between pt-1">
          <p className="text-[10px] text-muted-foreground">
            +{selectedAdditions.reduce((s, a) => s + a.calories, 0)} cal ajoutées
          </p>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-primary/30 text-primary"
            onClick={() => onConfirm(selectedAdditions)}
            data-testid="confirm-additions-btn"
          >
            <Check className="w-3 h-3 mr-1" />
            Confirmer
          </Button>
        </div>
      )}
    </div>
  );
}


export function RecurringMealCard({ onSelect, selectedDate }) {
  const { language } = useLanguage();
  const [recentMeals, setRecentMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(null);

  useEffect(() => {
    api.get('/meals/recent-unique?limit=6')
      .then(r => setRecentMeals(r.data.meals || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRelog = async (meal) => {
    // Iter. 406.fr.54 — optimistic "Comme hier" clone in one click.
    // Immediately mark the button as fired, notify the parent, and let the
    // network round-trip happen in the background. On failure, safePost
    // queues the mutation for auto-replay when the connection returns.
    setLogging(meal.meal_id);
    const optimisticPreview = {
      quick_logged: true,
      queued: true,
      meal: { ...meal, meal_id: `optimistic_${Date.now()}`, source_meal_id: meal.meal_id },
      message: '+1 clic',
    };
    if (onSelect) onSelect(optimisticPreview);
    toast.success(language === 'fr' ? 'Cloné ! Comme hier ✓' : 'Cloned! Like yesterday ✓', { duration: 1500 });
    try {
      const res = await safePost('/meals/quick-log', {
        meal_id: meal.meal_id,
        meal_type: meal.meal_type || 'repas',
        hidden_additions: [],
        target_date: selectedDate || undefined,
      });
      if (res?.queued) {
        toast.info(language === 'fr' ? 'Hors ligne — synchro auto au retour du réseau' : 'Offline — will sync when back online', { duration: 2200 });
      } else if (onSelect) {
        onSelect(res);
      }
    } finally {
      setLogging(null);
    }
  };

  if (loading) return null;
  if (recentMeals.length === 0) return null;

  // Iter. 411.i — Youri: "en plus on ne le voit pas très bien peut etre
  // refaire cette section en Glow et plus intéressante". Redesign avec:
  // - Amber/orange glow signature Extra Miles (fire aesthetic)
  // - Header dramatique avec Flame + count badge
  // - Tiles surélevées avec bordure lumineuse + hover scale
  // - Macros dans des pills contrastées (kcal orange, prot bleu)
  const isFr = language === 'fr';

  return (
    <div
      className="mb-4 rounded-2xl p-4 relative overflow-hidden"
      data-testid="recurring-meal-card"
      style={{
        background:
          'linear-gradient(135deg, rgba(251,146,60,0.12) 0%, rgba(234,88,12,0.06) 45%, rgba(0,0,0,0.4) 100%)',
        border: '1px solid rgba(251,146,60,0.42)',
        boxShadow:
          '0 0 22px rgba(251,146,60,0.28), inset 0 0 0 1px rgba(251,146,60,0.10)',
      }}
    >
      {/* Ambient corner flare */}
      <div
        className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(251,146,60,0.28) 0%, transparent 70%)' }}
      />

      <div className="flex items-center justify-between mb-3 relative">
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500/40 to-amber-600/25 flex items-center justify-center"
            style={{ boxShadow: '0 0 14px rgba(251,146,60,0.5)' }}
          >
            <RotateCcw
              className="w-4 h-4 text-orange-100"
              style={{ filter: 'drop-shadow(0 0 6px rgba(251,146,60,0.9))' }}
            />
          </div>
          <div>
            <p
              className="text-[11px] uppercase tracking-[0.25em] font-black text-orange-200"
              style={{ textShadow: '0 0 8px rgba(251,146,60,0.55)' }}
            >
              {isFr ? 'Comme hier' : 'Like yesterday'}
            </p>
            <p className="text-[10px] text-foreground/60 mt-0.5">
              {isFr ? '1 tap = repas cloné' : '1 tap = meal cloned'}
            </p>
          </div>
        </div>
        <span
          className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-orange-500/25 text-orange-200 border border-orange-400/40"
          data-testid="recurring-meal-count"
        >
          {recentMeals.length}
        </span>
      </div>

      <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide -mx-1 px-1">
        {recentMeals.map((meal) => {
          const active = logging === meal.meal_id;
          return (
            <button
              key={meal.meal_id}
              onClick={() => handleRelog(meal)}
              disabled={active}
              data-testid={`relog-meal-${meal.meal_id}`}
              className={`flex-shrink-0 min-w-[130px] max-w-[150px] p-3 rounded-xl text-left group relative overflow-hidden
                transition-all duration-200 active:scale-95 disabled:opacity-70`}
              style={{
                background: active
                  ? 'linear-gradient(140deg, rgba(251,146,60,0.28), rgba(234,88,12,0.14))'
                  : 'linear-gradient(140deg, rgba(0,0,0,0.55), rgba(15,15,15,0.6))',
                border: '1px solid rgba(251,146,60,0.32)',
                boxShadow: '0 0 10px rgba(251,146,60,0.15), inset 0 1px 0 rgba(255,255,255,0.04)',
              }}
            >
              {active ? (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-orange-300" />
                </div>
              ) : (
                <>
                  <p
                    className="text-xs font-bold text-foreground truncate leading-tight mb-2 group-hover:text-orange-200 transition-colors"
                    title={meal.meal_name || 'Repas'}
                  >
                    {meal.meal_name || (isFr ? 'Repas' : 'Meal')}
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className="text-[10px] font-mono font-black px-1.5 py-0.5 rounded-md bg-orange-500/25 text-orange-200 border border-orange-400/30"
                      style={{ textShadow: '0 0 4px rgba(251,146,60,0.6)' }}
                    >
                      {Math.round(meal.total_calories || 0)} cal
                    </span>
                    <span
                      className="text-[10px] font-mono font-black px-1.5 py-0.5 rounded-md bg-sky-500/25 text-sky-200 border border-sky-400/30"
                      style={{ textShadow: '0 0 4px rgba(59,130,246,0.55)' }}
                    >
                      {Math.round(meal.total_protein_g || 0)}g P
                    </span>
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}


export function RecipeQuickLogButton({ recipes = [], onLogRecipe, selectedDate }) {
  const [expanded, setExpanded] = useState(false);
  const [logging, setLogging] = useState(null);
  const { language } = useLanguage();

  if (recipes.length === 0) return null;

  const handleLog = async (recipe) => {
    setLogging(recipe.recipe_id);
    try {
      const res = await api.post('/meals/quick-log', {
        recipe_id: recipe.recipe_id,
        meal_type: recipe.meal_type || 'repas',
        hidden_additions: [],
        target_date: selectedDate || undefined,
      });
      toast.success(res.data.message || 'Repas logué!');
      if (onLogRecipe) onLogRecipe(res.data);
    } catch {
      toast.error('Erreur');
    } finally {
      setLogging(null);
    }
  };

  const topRecipes = expanded ? recipes : recipes.slice(0, 3);

  return (
    <div className="space-y-2" data-testid="recipe-quick-log">
      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider flex items-center gap-1">
        <ChefHat className="w-3 h-3" />
        {language === 'fr' ? 'Mes Recettes' : 'My Recipes'}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {topRecipes.map((recipe) => (
          <Button
            key={recipe.recipe_id}
            variant="outline"
            size="sm"
            className="h-8 text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            disabled={logging === recipe.recipe_id}
            onClick={() => handleLog(recipe)}
            data-testid={`quick-recipe-${recipe.recipe_id}`}
          >
            {logging === recipe.recipe_id ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <Zap className="w-3 h-3 mr-1" />
            )}
            {recipe.name}
          </Button>
        ))}
        {recipes.length > 3 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Moins' : `+${recipes.length - 3}`}
          </Button>
        )}
      </div>
    </div>
  );
}
